import React, { useState } from 'react';
import { Stethoscope, FileText, Headphones, ChevronRight, ChevronLeft } from 'lucide-react';

const CareHistory = ({ formData, updateFormData, t, onSubStepChange, onBackPress }) => {
  const [subStep, setSubStep] = useState(0); // 0: consulted, 1: audiogram, 2: hearing_aids
  
  // Notify parent of sub-step changes
  React.useEffect(() => {
    if (onSubStepChange) {
      onSubStepChange(subStep, 2); // current step, total steps
    }
  }, [subStep, onSubStepChange]);
  
  // Handle back press from parent (header back arrow)
  React.useEffect(() => {
    if (onBackPress) {
      const handleBack = () => {
        if (subStep > 0) {
          setSubStep(prev => prev - 1);
          return true; // Handled internally
        }
        return false; // Let parent handle it (go to previous major step)
      };
      onBackPress(handleBack);
    }
  }, [subStep, onBackPress]);

  const consultedOptions = [
    { value: 'orl', label: 'ORL', icon: '👨‍⚕️' },
    { value: 'audiologist', label: 'Audioprothésiste', icon: '👂' },
    { value: 'gp', label: 'Médecin généraliste', icon: '🩺' },
    { value: 'none', label: 'Personne', icon: '✖' },
  ];

  const audiogramOptions = [
    { value: 'yes', label: 'Oui', icon: '✓', desc: 'J\'ai réalisé un audiogramme' },
    { value: 'no', label: 'Non', icon: '✗', desc: 'Je n\'ai pas fait d\'audiogramme' },
    { value: 'unknown', label: 'Je ne sais pas', icon: '?', desc: 'Je ne suis pas sûr(e)' },
  ];

  const toggleConsulted = (value) => {
    const current = formData.consulted || [];
    
    // Special handling for "none"
    if (value === 'none') {
      if (current.includes('none')) {
        updateFormData({ consulted: [] });
      } else {
        updateFormData({ consulted: ['none'] });
      }
      return;
    }
    
    // If selecting another option, remove "none"
    let newConsulted = current.filter(v => v !== 'none');
    
    if (newConsulted.includes(value)) {
      newConsulted = newConsulted.filter(v => v !== value);
    } else {
      newConsulted = [...newConsulted, value];
    }
    
    updateFormData({ consulted: newConsulted });
  };

  const canGoNext = () => {
    if (subStep === 0) return (formData.consulted || []).length > 0;
    if (subStep === 1) return !!formData.audiogram_done;
    if (subStep === 2) return formData.hearing_aids !== undefined;
    return false;
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-block p-3 bg-green-100 rounded-2xl">
          <Stethoscope size={32} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          Suivi & Prise en charge
        </h2>
        <p className="text-sm text-gray-600">
          Parlez-nous de votre parcours de soins
        </p>
        {/* Sub-step indicator */}
        <div className="flex justify-center gap-2 pt-2">
          {[0, 1, 2].map((step) => (
            <div
              key={step}
              className={`h-2 rounded-full transition-all ${
                step === subStep
                  ? 'w-8 bg-blue-500'
                  : step < subStep
                  ? 'w-2 bg-blue-300'
                  : 'w-2 bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Sub-step 0: Consulted */}
      {subStep === 0 && (
        <div className="space-y-4">
          <p className="text-center text-gray-600 text-lg">
            Avez-vous consulté un professionnel de santé ?
          </p>
          <p className="text-center text-sm text-gray-500">
            (vous pouvez en choisir plusieurs)
          </p>
          <div className="grid grid-cols-2 gap-3">
            {consultedOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => toggleConsulted(option.value)}
                className={`p-4 rounded-2xl font-medium transition-all ${
                  (formData.consulted || []).includes(option.value)
                    ? 'bg-green-500 text-white shadow-lg ring-2 ring-green-500 ring-offset-2'
                    : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-green-300'
                }`}
              >
                <div className="text-2xl mb-1">{option.icon}</div>
                <div className="text-sm">{option.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sub-step 1: Audiogram */}
      {subStep === 1 && (
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <FileText size={20} className="text-green-600" />
          </div>
          <p className="text-center text-gray-600 text-lg">
            Avez-vous réalisé un audiogramme ?
          </p>
          <p className="text-center text-xs text-gray-500">
            (test d'audition professionnel)
          </p>
          <div className="space-y-3">
            {audiogramOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => updateFormData({ audiogram_done: option.value })}
                className={`w-full p-4 rounded-2xl font-medium transition-all text-left ${
                  formData.audiogram_done === option.value
                    ? 'bg-green-500 text-white shadow-lg ring-2 ring-green-500 ring-offset-2'
                    : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-green-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="text-2xl">{option.icon}</div>
                  <div>
                    <div className="font-semibold">{option.label}</div>
                    <div className={`text-xs mt-1 ${
                      formData.audiogram_done === option.value ? 'text-white/80' : 'text-gray-500'
                    }`}>
                      {option.desc}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          
          {/* Info Card */}
          {formData.audiogram_done === 'unknown' && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mt-4">
              <p className="text-sm text-blue-800">
                💡 <strong>Bon à savoir</strong> : Vous pourrez passer un test audio directement dans l'application HEAREN.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Sub-step 2: Hearing Aids */}
      {subStep === 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Headphones size={20} className="text-green-600" />
          </div>
          <p className="text-center text-gray-600 text-lg">
            Portez-vous des appareils auditifs ?
          </p>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => updateFormData({ hearing_aids: true })}
              className={`p-5 rounded-2xl font-medium transition-all ${
                formData.hearing_aids === true
                  ? 'bg-green-500 text-white shadow-lg ring-2 ring-green-500 ring-offset-2'
                  : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-green-300'
              }`}
            >
              <div className="text-3xl mb-2">✓</div>
              <div className="font-semibold">Oui</div>
              <div className={`text-xs mt-1 ${
                formData.hearing_aids === true ? 'text-white/80' : 'text-gray-500'
              }`}>
                Je porte des appareils
              </div>
            </button>
            <button
              onClick={() => updateFormData({ hearing_aids: false })}
              className={`p-5 rounded-2xl font-medium transition-all ${
                formData.hearing_aids === false
                  ? 'bg-green-500 text-white shadow-lg ring-2 ring-green-500 ring-offset-2'
                  : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-green-300'
              }`}
            >
              <div className="text-3xl mb-2">✗</div>
              <div className="font-semibold">Non</div>
              <div className={`text-xs mt-1 ${
                formData.hearing_aids === false ? 'text-white/80' : 'text-gray-500'
              }`}>
                Je n'en porte pas
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Navigation button */}
      {subStep < 2 && (
        <div className="pt-4">
          <button
            onClick={() => setSubStep(subStep + 1)}
            disabled={!canGoNext()}
            className={`w-full py-4 rounded-2xl font-semibold transition-all flex items-center justify-center gap-2 ${
              canGoNext()
                ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Suivant
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default CareHistory;